// pages/CreateProgram/index.jsx
import { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import api from "../../../../../Axios.js";

import Step1Basic from "./Step1Basic";
import Step2Schedule from "./Step2Schedule";
import Step3Modules from "./Step3Modules";
import Step4Skills from "./Step4Skills";
import Step5Review from "./Step5Review";
import Stepper from "../../../../../components/learning-development/common/Stepper.jsx";
import { IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const steps = [1, 2, 3, 4, 5];

export default function CreateProgram() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialProgramId =
    params?.program_id ||
    params?.id ||
    location?.state?.program_id ||
    location?.state?.programId ||
    null;

  const [step, setStep] = useState(1);
  const [programId, setProgramId] = useState(initialProgramId);
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [masterRender, setMasterRender] = useState(false);
  const [completedStage, setCompletedStage] = useState(0);

  const next = () => setStep((s) => Math.min(s + 1, 5));
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  // Load program data in parent when programId is present (edit mode)
  useEffect(() => {
    let cancelled = false;

    async function fetchProgram(id) {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/lnd/${id}/preview`);
        if (!cancelled) {
          setInitialData(res.data.data.program || null);
          setCompletedStage(res.data.data.program.completed_stage || 0);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (programId) fetchProgram(programId);

    return () => {
      cancelled = true;
    };
  }, [programId, masterRender]);

  return (
    <div className="container my-4">

      {/* HEADER ROW */}
      <div className="d-flex align-items-center justify-content-between mb-3">

        <div className="d-flex align-items-center gap-3">
          <Tooltip title="Back" placement="top" arrow>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                backgroundColor: "#f5f5f5",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <h4 className="mb-0">
            {programId ? "Edit Learning Program" : "Create Learning Program"}
          </h4>
        </div>

      </div>

      {/* STEPPER */}
      <Stepper
        steps={steps}
        currentStep={step}
        completedStep={completedStage}
        onStepClick={(clickedStep) => {
          if ((clickedStep <= completedStage) || (Number(clickedStep) === 5 && Number(completedStage) === 4)) {
            setStep(clickedStep);
          }
        }}
      />

      {loading ? (
        <div className="mt-4">Loading program...</div>
      ) : (
        <>
          {step === 1 && (
            <Step1Basic
              programId={programId}
              setProgramId={setProgramId}
              initialData={initialData}
              setMasterRender={setMasterRender}
              next={next}
            />
          )}

          {step === 2 && (
            <Step2Schedule
              programId={programId}
              initialData={initialData}
              setMasterRender={setMasterRender}
              next={next}
              prev={prev}
            />
          )}

          {step === 3 && (
            <Step3Modules
              programId={programId}
              initialData={initialData}
              setMasterRender={setMasterRender}
              next={next}
              prev={prev}
            />
          )}

          {step === 4 && (
            <Step4Skills
              programId={programId}
              initialData={initialData}
              setMasterRender={setMasterRender}
              next={next}
              prev={prev}
            />
          )}

          {step === 5 && (
            <Step5Review
              programId={programId}
              initialData={initialData}
              setMasterRender={setMasterRender}
              prev={prev}
            />
          )}
        </>
      )}

      {error && (
        <div className="alert alert-danger mt-3">
          Something went wrong while loading the program.
        </div>
      )}

    </div>
  );
}